/**
 * Utilidades para mapear preguntas/respuestas a opciones de filtros dinámicos
 * Transforma datos del API de preguntas en formato de opciones para selectores
 */

/**
 * Mapea un array de preguntas a opciones de filtros (Empresa, Área, Cargo)
 * Identifica las preguntas por su orden o por coincidencia de texto
 * @param {Array} preguntas - Array de preguntas del API
 * @returns {Object} { companies: [], areas: [], positions: [] }
 * 
 * @example
 * const preguntas = [
 *   { orden: 1, pregunta: "Empresa contratante", respuestas: [...] },
 *   { orden: 2, pregunta: "Área", respuestas: [...] },
 *   { orden: 3, pregunta: "Cargo", respuestas: [...] }
 * ];
 * const filtros = mapPreguntasToFilters(preguntas);
 * // Retorna: { companies: [...], areas: [...], positions: [...] }
 */
export const mapPreguntasToFilters = (preguntas) => {
	const filters = {
		companies: [],
		areas: [],
		positions: []
	};

	if (!preguntas || !Array.isArray(preguntas)) {
		return filters;
	}

	//Es para el selector de empresa
	const preguntaEmpresa = getItemWithLowestOrder(preguntas);
	const respuestas = preguntaEmpresa.respuestas || [];
	const opciones = respuestas.map(r => ({
			id: r.respuesta,
			label: r.respuesta,
			value: r.respuesta,
			siguiente: r.siguiente
		}));
	filters.companies = opciones;
	return filters;
};

/**
 * Obtiene una pregunta específica por su número de orden
 * @param {Array} preguntas - Array de preguntas del API
 * @param {number} orden - Número de orden a buscar (1, 2, 3, etc.)
 * @returns {Object|null} La pregunta encontrada o null
 * 
 * @example
 * const preguntaEmpresa = getPreguntaByOrden(preguntas, 1);
 * // Retorna: { id: 5, pregunta: "Empresa contratante", orden: 1, respuestas: [...] }
 */
export const getPreguntaByOrden = (preguntas, orden) => {
	if (!preguntas || !Array.isArray(preguntas)) {
		return null;
	}
	return preguntas.find(p => p && p.orden === orden) || null;
};

/**
 * Obtiene una pregunta por coincidencia de texto (case-insensitive)
 * @param {Array} preguntas - Array de preguntas del API
 * @param {string} texto - Texto a buscar en la pregunta
 * @returns {Object|null} La pregunta encontrada o null
 * 
 * @example
 * const preguntaEmpresa = getPreguntaByTexto(preguntas, 'empresa');
 * // Retorna: { id: 5, pregunta: "Empresa contratante", orden: 1, respuestas: [...] }
 */
export const getPreguntaByTexto = (preguntas, texto) => {
	if (!preguntas || !Array.isArray(preguntas) || !texto) {
		return null;
	}
	const textoLower = texto.toLowerCase();
	return preguntas.find(p => 
		p && p.pregunta && p.pregunta.toLowerCase().includes(textoLower)
	) || null;
};

/**
 * Obtiene las respuestas de una pregunta específica
 * @param {Array} preguntas - Array de preguntas del API
 * @param {number} orden - Número de orden de la pregunta
 * @returns {Array} Array de respuestas formateadas como opciones
 * 
 * @example
 * const opcionesEmpresa = getRespuestasByOrden(preguntas, 1);
 * // Retorna: [
 * //   { id: 9, label: "(COMER) Comercializadora Nal. SAS", value: "...", siguiente: 6 },
 * //   { id: 10, label: "(PAC) Pepsico Alimentos Antioquia LTDA", value: "...", siguiente: 6 }
 * // ]
 */
export const getRespuestasByOrden = (preguntas, orden) => {
	const pregunta = getPreguntaByOrden(preguntas, orden);
	if (!pregunta || !pregunta.respuestas) {
		return [];
	}
	return pregunta.respuestas.map(r => ({
		id: r.id,
		label: r.respuesta,
		value: r.respuesta,
		siguiente: r.siguiente
	}));
};

/**
 * Obtiene las respuestas de una pregunta por coincidencia de texto
 * @param {Array} preguntas - Array de preguntas del API
 * @param {string} texto - Texto a buscar en la pregunta
 * @returns {Array} Array de respuestas formateadas como opciones
 * 
 * @example
 * const opcionesArea = getRespuestasByTexto(preguntas, 'área');
 * // Retorna: [{ id: 15, label: "Ventas", value: "Ventas", siguiente: 7 }, ...]
 */
export const getRespuestasByTexto = (preguntas, texto) => {
	const pregunta = getPreguntaByTexto(preguntas, texto);
	if (!pregunta || !pregunta.respuestas) {
		return [];
	}
	return pregunta.respuestas.map(r => ({
		id: r.id,
		label: r.respuesta,
		value: r.respuesta,
		siguiente: r.siguiente
	}));
};

/**
 * Valida si una pregunta tiene respuestas
 * @param {Object} pregunta - Pregunta a validar
 * @returns {boolean} true si tiene respuestas, false en caso contrario
 * 
 * @example
 * const tieneRespuestas = hasPreguntaRespuestas(pregunta);
 */
export const hasPreguntaRespuestas = (pregunta) => {
	return pregunta && 
		   Array.isArray(pregunta.respuestas) && 
		   pregunta.respuestas.length > 0;
};

export const getItemWithLowestOrder = (list = []) => {
  if (!Array.isArray(list) || list.length === 0) return null;

  return list.reduce((min, item) => {
    return item.orden < min.orden ? item : min;
  }, list[0]);
};

/**
 * Obtiene items de una lista que coincidan con un orden específico
 * Útil para obtener preguntas por su número de orden
 * @param {Array} list - Array de items
 * @param {number} targetOrder - Orden a buscar
 * @returns {Array} Array de items que coinciden con el orden
 * 
 * @example
 * const areasPreguntas = getItemsByOrder(preguntas, 2);
 * // Retorna preguntas con orden === 2
 */
export const getItemsByOrder = (list = [], targetOrder) => {
  if (!Array.isArray(list) || typeof targetOrder !== 'number') {
    return [];
  }

  return list.filter(item => item.id === targetOrder);
};


/**
 * Obtiene la siguiente pregunta basada en la respuesta seleccionada
 * Útil para validaciones futuras de flujos condicionales
 * @param {Array} preguntas - Array de preguntas del API
 * @param {number} siguienteId - ID de la siguiente pregunta
 * @returns {Object|null} La siguiente pregunta o null
 * 
 * @example
 * const siguientePregunta = getSiguientePregunta(preguntas, 6);
 * // Retorna la pregunta con id: 6
 */
export const getSiguientePregunta = (preguntas, siguienteId) => {
	if (!preguntas || !Array.isArray(preguntas) || !siguienteId) {
		return null;
	}
	return preguntas.find(p => p && p.id === siguienteId) || null;
};

/**
 * Obtiene las opciones de filtro para el siguiente nivel en cascada
 * Basado en el campo 'siguiente' de la respuesta seleccionada
 * @param {Array} preguntas - Array de todas las preguntas
 * @param {number} siguienteOrden - Orden de la siguiente pregunta (del campo 'siguiente')
 * @returns {Array} Array de opciones formateadas [{id, label, value, siguiente}]
 * 
 * @example
 * const opcionesArea = getOpcionesEnCascada(preguntas, 2);
 * // Retorna las respuestas de la pregunta con orden === 2
 */
export const getOpcionesEnCascada = (preguntas, siguienteOrden) => {
	if (!preguntas || !Array.isArray(preguntas) || typeof siguienteOrden !== 'number') {
		return [];
	}

	const preguntasSiguientes = getItemsByOrder(preguntas, siguienteOrden);
	if (preguntasSiguientes.length === 0) {
		return [];
	}

	const pregunta = preguntasSiguientes[0];
	const respuestas = pregunta.respuestas || [];
	
	return respuestas.map(r => ({
		id: r.respuesta,
		label: r.respuesta,
		value: r.respuesta,
		siguiente: r.siguiente
	}));
};

export default {
	mapPreguntasToFilters,
	getPreguntaByOrden,
	getPreguntaByTexto,
	getRespuestasByOrden,
	getRespuestasByTexto,
	hasPreguntaRespuestas,
	getSiguientePregunta,
	getItemWithLowestOrder,
	getItemsByOrder,
	getOpcionesEnCascada
};
